import type { GridNode } from '@react-types/grid';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { useCollator } from 'react-aria';
import { Cell, Column, Row, TableBody, TableHeader } from 'react-stately';

import { getErrorMessage } from '../../../../lib/errors';
import { HomeyApiKind } from '../../../../lib/HomeyApiKind';
import { SCOPES } from '../../../../lib/scopes';
import { SettingsTable } from '../SettingsTable.jsx';

import { devicesStore } from '../../../../store/devices/DevicesStore.ts';
import type { V3DeviceProps } from '../../../../store/devices/DevicesStore.ts';
import { zonesStore } from '../../../../store/zones/ZonesStore.ts';

import { useI18n } from '../../../../hooks/useI18nFormatters';
import { useDevicesById } from '../../../../store/devices/useDevices.ts';
import { useApi } from '../../../../store/SelectedHomeyStore.hooks';
import { useZonesById } from '../../../../store/zones/useZones.ts';

import { glyph } from '../../../../theme/glyph.ts';
import { theme } from '../../../../theme/theme';
import styles from './DeviceUpdatesSettings.module.css';

import { FlatButton } from '../../../../components/buttons/FlatButton';
import { CenteredMessage } from '../../../../components/common/CenteredMessage';
import { Glyph } from '../../../../components/common/Glyph';
import { RoundIconWrapper } from '../../../../components/common/RoundIconWrapper';
import { SpinnerLoading } from '../../../../components/common/SpinnerLoading';

import { DeviceUpdatesChangelogDialog } from './DeviceUpdatesChangelogDialog.tsx';
import { DeviceUpdatesSettingsTableCell } from './DeviceUpdatesSettingsTableCell.tsx';
import { DeviceUpdatesSettingsTableColumnHeader } from './DeviceUpdatesSettingsTableColumnHeader.jsx';
import { DeviceUpdatesSettingsTableRow } from './DeviceUpdatesSettingsTableRow.tsx';

const IN_PROGRESS_FIRMWARE_UPDATE_STATES = new Set(['installing', 'aborting', 'queued']);
const AVAILABLE_FIRMWARE_UPDATE_STATES = new Set(['available', 'error']);

export function DeviceUpdatesSettings() {
  const { i18n } = useI18n();
  const { scopes } = useApi();
  const collator = useCollator();

  const devices = useDevicesById({ apiKinds: [HomeyApiKind.V3_LOCAL, HomeyApiKind.V3_CLOUD] });
  const zones = useZonesById();

  const [changelogInfo, setChangelogInfo] = useState<{
    deviceId: string;
    availableVersion: string;
    currentVersion: string;
  } | null>(null);

  const { available, inProgress } = useMemo(() => {
    const nextInProgress: V3DeviceProps[] = [];
    const nextAvailable: V3DeviceProps[] = [];

    for (const device of Object.values(devices.byId ?? {})) {
      const firmwareUpdateState = device.firmwareUpdate?.state ?? null;

      if (IN_PROGRESS_FIRMWARE_UPDATE_STATES.has(firmwareUpdateState ?? '')) {
        nextInProgress.push(device);
      } else if (AVAILABLE_FIRMWARE_UPDATE_STATES.has(firmwareUpdateState ?? '')) {
        nextAvailable.push(device);
      }
    }

    nextInProgress.sort((left, right) => {
      return compareInProgressDevices(left, right, collator);
    });
    nextAvailable.sort((left, right) => {
      return compareAvailableDevices(left, right, collator);
    });

    return {
      available: nextAvailable,
      inProgress: nextInProgress,
    };
  }, [collator, devices.byId]);

  if (scopes[SCOPES.UPDATES_READONLY] !== true) {
    return (
      <div className={styles.root}>
        <CenteredMessage
          title={i18n.messageFormatter('common.insufficientPermissions')}
          subtitleColor={theme.color.text_light}
          subtitle={i18n.messageFormatter('common.insufficientPermissionsViewSubtitle', {
            name: i18n.messageFormatter('settings.system.deviceUpdates'),
          })}
        />
      </div>
    );
  }

  if (devices.isLoading === true || zones.isLoading === true) {
    return (
      <div className={styles.root}>
        <CenteredMessage
          icon={<SpinnerLoading size={24} rainbow={false} color={theme.color.text_light} />}
          subtitleColor={theme.color.text_light}
          subtitle={i18n.messageFormatter('settings.system.deviceUpdatesCheckingForUpdates')}
        />
      </div>
    );
  }

  if (devices.error != null || zones.error != null) {
    return (
      <div className={styles.root}>
        <CenteredMessage
          title="Error"
          subtitle={
            getErrorMessage(devices.error) ??
            getErrorMessage(zones.error) ??
            i18n.messageFormatter('common.unknown')
          }
          action={
            <FlatButton
              styleColor="blue"
              onPress={() => {
                if (devices.error != null) {
                  devicesStore.retry().catch(console.error);
                }

                if (zones.error != null) {
                  zonesStore.retry().catch(console.error);
                }
              }}
            >
              {i18n.messageFormatter('common.retry')}
            </FlatButton>
          }
        />
      </div>
    );
  }

  if (available.length === 0 && inProgress.length === 0) {
    return (
      <div className={styles.root}>
        <CenteredMessage
          icon={
            <RoundIconWrapper size="60px" color={theme.color.green}>
              <Glyph
                source={glyph.core.checkmarkCircleFill}
                size={theme.icon.size_xlarge}
                color={theme.color.white}
              />
            </RoundIconWrapper>
          }
          title={i18n.messageFormatter('settings.system.deviceUpdatesUpToDateTitle')}
        />
      </div>
    );
  }

  function renderInProgressCell() {
    return <DeviceUpdatesSettingsTableCell type="in-progress" />;
  }

  function renderAvailableCell({ row }: { row: GridNode<V3DeviceProps> }) {
    const rowValue = row.value;
    const availableVersion = rowValue?.firmwareUpdate?.availableVersion;
    const currentVersion = rowValue?.firmwareUpdate?.currentVersion;
    const hasValidVersions =
      typeof availableVersion === 'string' &&
      availableVersion.trim() !== '' &&
      typeof currentVersion === 'string' &&
      currentVersion.trim() !== '';

    return (
      <DeviceUpdatesSettingsTableCell
        type="available"
        onVersionPress={
          hasValidVersions
            ? () => {
                if (rowValue == null) {
                  return;
                }

                setChangelogInfo({
                  deviceId: rowValue.id,
                  availableVersion,
                  currentVersion,
                });
              }
            : undefined
        }
      />
    );
  }

  function renderColumnHeader() {
    return <DeviceUpdatesSettingsTableColumnHeader />;
  }

  function renderDeviceUpdatesRow({ children }: { children: ReactNode }) {
    return <DeviceUpdatesSettingsTableRow>{children}</DeviceUpdatesSettingsTableRow>;
  }

  return (
    <div className={styles.root}>
      {inProgress.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>
            {i18n.messageFormatter('settings.system.deviceUpdatesInProgress')}
          </h2>

          <SettingsTable
            aria-label={i18n.messageFormatter('settings.system.deviceUpdatesInProgress')}
            isFullWidth={true}
            selectionMode="none"
            renderRow={renderDeviceUpdatesRow}
            renderCell={renderInProgressCell}
            renderColumnHeader={renderColumnHeader}
          >
            <TableHeader
              columns={[
                {
                  key: 'device',
                  name: i18n.messageFormatter('settings.system.deviceUpdatesColumnDevice'),
                },
                {
                  key: 'zone',
                  name: i18n.messageFormatter('settings.system.deviceUpdatesColumnZone'),
                },
                { key: 'state', name: '' },
                { key: 'actions', name: '' },
              ]}
            >
              {(column) => {
                return <Column>{column.name}</Column>;
              }}
            </TableHeader>

            <TableBody items={inProgress}>
              {(item) => {
                return (
                  <Row>
                    {(columnKey) => {
                      switch (columnKey) {
                        case 'device': {
                          return <Cell>{item.name}</Cell>;
                        }

                        case 'zone': {
                          const zoneName = zones.byId?.[item.zone ?? '']?.name;

                          return <Cell>{zoneName}</Cell>;
                        }

                        case 'state': {
                          const state = item.firmwareUpdate?.state ?? '-';

                          return <Cell>{state}</Cell>;
                        }

                        default:
                          return <Cell children={undefined} />;
                      }
                    }}
                  </Row>
                );
              }}
            </TableBody>
          </SettingsTable>
        </section>
      )}

      {available.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>
            {i18n.messageFormatter('settings.system.deviceUpdatesAvailable')}{' '}
            <span className={styles.sectionCount}>({available.length})</span>
          </h2>

          <SettingsTable
            aria-label={i18n.messageFormatter('settings.system.deviceUpdatesAvailable')}
            isFullWidth={true}
            selectionMode="none"
            renderRow={renderDeviceUpdatesRow}
            renderCell={renderAvailableCell}
            renderColumnHeader={renderColumnHeader}
          >
            <TableHeader
              columns={[
                {
                  key: 'device',
                  name: i18n.messageFormatter('settings.system.deviceUpdatesColumnDevice'),
                },
                {
                  key: 'zone',
                  name: i18n.messageFormatter('settings.system.deviceUpdatesColumnZone'),
                },
                {
                  key: 'version',
                  name: i18n.messageFormatter('settings.system.deviceUpdatesColumnVersion'),
                },
                { key: 'actions', name: '' },
              ]}
            >
              {(column) => {
                return <Column>{column.name}</Column>;
              }}
            </TableHeader>

            <TableBody items={available}>
              {(item) => {
                return (
                  <Row>
                    {(columnKey) => {
                      switch (columnKey) {
                        case 'device': {
                          return <Cell>{item.name}</Cell>;
                        }

                        case 'zone': {
                          const zoneName = zones.byId?.[item.zone ?? '']?.name;

                          return <Cell>{zoneName}</Cell>;
                        }

                        case 'version': {
                          const version = item.firmwareUpdate?.availableVersion ?? '-';

                          return <Cell>{version}</Cell>;
                        }

                        default:
                          return <Cell children={undefined} />;
                      }
                    }}
                  </Row>
                );
              }}
            </TableBody>
          </SettingsTable>
        </section>
      )}

      {changelogInfo != null && (
        <DeviceUpdatesChangelogDialog
          availableVersion={changelogInfo.availableVersion}
          currentVersion={changelogInfo.currentVersion}
          deviceId={changelogInfo.deviceId}
          onClose={() => {
            setChangelogInfo(null);
          }}
        />
      )}
    </div>
  );
}

DeviceUpdatesSettings.pathKey = 'system/device-updates';
DeviceUpdatesSettings.icon = glyph.system.ota;
DeviceUpdatesSettings.backgroundColor = theme.color.system_ota;

function compareInProgressDevices(
  left: V3DeviceProps,
  right: V3DeviceProps,
  collator: Intl.Collator,
) {
  const stateComparison = getInProgressStateOrder(left) - getInProgressStateOrder(right);

  if (stateComparison !== 0) {
    return stateComparison;
  }

  return compareDeviceNames(left, right, collator);
}

function compareAvailableDevices(
  left: V3DeviceProps,
  right: V3DeviceProps,
  collator: Intl.Collator,
) {
  const stateComparison = getAvailableStateOrder(left) - getAvailableStateOrder(right);

  if (stateComparison !== 0) {
    return stateComparison;
  }

  return compareDeviceNames(left, right, collator);
}

function getInProgressStateOrder(device: V3DeviceProps) {
  switch (device.firmwareUpdate?.state) {
    case 'installing':
    case 'aborting':
      return 0;

    case 'queued':
      return 1;

    default:
      return 2;
  }
}

function getAvailableStateOrder(device: V3DeviceProps) {
  if (device.firmwareUpdate?.state === 'error') {
    return 0;
  }

  return 1;
}

function compareDeviceNames(left: V3DeviceProps, right: V3DeviceProps, collator: Intl.Collator) {
  const nameComparison = collator.compare(String(left.name ?? ''), String(right.name ?? ''));

  if (nameComparison !== 0) {
    return nameComparison;
  }

  return String(left.id).localeCompare(String(right.id));
}
